import { useEffect, useState, type FormEvent } from "react";
import { invokeAiModel, type AiModelInvokeResult } from "../api/client";
import { VENDOR_PO_PRODUCTS, vendorPoItemsPayload } from "../content/vendorPoProducts";

const SAMPLE_STORE_LABEL = "allpet · Location 9999";
const SAMPLE_VENDOR_NAME = "Menu Food Manufacturing";
const INVENTORY_MODEL_ID = "allpet-inventory-predict";

type PredictionIntervals = {
  confidence_level?: number;
  lower?: number;
  prediction?: number;
  upper?: number;
};

type InventoryPrediction = {
  item_name?: string;
  inventory_id?: number;
  current_quantity?: number;
  predicted_inventory_quantity?: number;
  recommendation?: string;
  confidence_score?: number;
  stockout_risk?: number;
  overstock_risk?: number;
  prediction_intervals?: PredictionIntervals;
};

type InventoryPredictPayload = {
  location_id?: number;
  status?: string;
  predictions?: InventoryPrediction[];
};

type VendorOrderLine = {
  item_name?: string;
  quantity_to_order?: number;
  unit_cost?: number;
  unit_price?: number;
  line_total?: number;
};

type VendorMinPoPayload = {
  meets_minimum?: boolean;
  minimum_order_value?: number;
  order_items?: VendorOrderLine[];
};

type StockSltpPayload = Record<string, unknown>;

type WebTrafficIpRow = {
  ip?: string;
  count?: number;
  country?: string;
  region?: string;
  city?: string;
  geo_status?: string;
  geolocation?: string;
  predicted_class?: string;
  confidence?: number;
  probabilities?: {
    benign?: number;
    scan?: number;
  };
  window?: {
    method?: string;
    path_prefix?: string;
    request_count?: number;
    status_2xx?: number;
    status_4xx?: number;
    status_5xx?: number;
    ua_family?: string;
    window_start?: string;
  };
};

type WebTrafficThreatPayload = {
  status?: string;
  client_name?: string;
  model_type?: string;
  predicted_class?: string;
  confidence?: number;
  label_provenance?: string;
  ml_enabled?: boolean;
  request_count?: number;
  log_range?: {
    start?: string;
    end?: string;
  };
  probabilities?: {
    benign?: number;
    scan?: number;
  };
  by_ip?: WebTrafficIpRow[];
  metrics_test?: {
    metrics_trusted?: boolean;
    n_test?: number;
    n_train?: number;
    macro_f1?: number;
  };
  data_source?: {
    n_raw_events?: number;
    n_windows?: number;
    data_source_kind?: string;
  };
};

function isInventoryPredict(data: unknown): data is InventoryPredictPayload {
  if (!data || typeof data !== "object") return false;
  return Array.isArray((data as InventoryPredictPayload).predictions);
}

function isVendorMinPo(data: unknown): data is VendorMinPoPayload {
  if (!data || typeof data !== "object") return false;
  const o = data as VendorMinPoPayload;
  return Array.isArray(o.order_items) && typeof o.minimum_order_value === "number";
}

function isStockSltp(data: unknown): data is StockSltpPayload {
  if (!data || typeof data !== "object") return false;
  const o = data as StockSltpPayload;
  return "position_action" in o || "predicted_direction" in o || "stock" in o;
}

function isWebTrafficThreat(data: unknown): data is WebTrafficThreatPayload {
  if (!data || typeof data !== "object" || Array.isArray(data)) return false;
  const o = data as WebTrafficThreatPayload;
  if (o.model_type === "webtraffic_threat") return true;
  return (
    typeof o.predicted_class === "string" &&
    o.probabilities != null &&
    typeof o.probabilities === "object"
  );
}

function fmtMoney(n: number | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function fmtUnits(n: number | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return Math.round(n).toLocaleString();
}

function fmtPct01(n: number | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

function fmtPctNumber(n: number | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${n.toFixed(2)}%`;
}

function fmtPctWhole(n: number | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
}

function num(data: StockSltpPayload, key: string): number | undefined {
  const v = data[key];
  return typeof v === "number" && !Number.isNaN(v) ? v : undefined;
}

function bool(data: StockSltpPayload, key: string): boolean | undefined {
  const v = data[key];
  return typeof v === "boolean" ? v : undefined;
}

function str(data: StockSltpPayload, key: string): string | undefined {
  const v = data[key];
  return typeof v === "string" ? v : undefined;
}

function recommendationLabel(rec: string | undefined): { label: string; tone: string } {
  const key = (rec ?? "").toLowerCase();
  if (key === "increase") {
    return { label: "Order more — you may run short at current levels", tone: "rec-increase" };
  }
  if (key === "decrease") {
    return { label: "Order less — you may be carrying too much", tone: "rec-decrease" };
  }
  if (key === "hold" || key === "maintain") {
    return { label: "Hold steady — levels look reasonable", tone: "rec-hold" };
  }
  return { label: rec ?? "Review with your team", tone: "rec-neutral" };
}

function humanizeAction(action: string | undefined): string {
  if (!action) return "Review position";
  return action.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function ReportShell({
  kicker,
  title,
  subtitle,
  children,
}: {
  kicker?: string;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  const showHeader = Boolean(kicker || title || subtitle);
  return (
    <div className="inventory-report">
      {showHeader && (
        <header className="inventory-report-header">
          {kicker && <p className="inventory-report-kicker">{kicker}</p>}
          {title && <h2 className="inventory-report-title">{title}</h2>}
          {subtitle && <p className="inventory-report-meta">{subtitle}</p>}
        </header>
      )}
      {children}
    </div>
  );
}

function InventoryPredictReport({
  data,
  resultMeta,
  onResultChange,
}: {
  data: InventoryPredictPayload;
  resultMeta: AiModelInvokeResult;
  onResultChange?: (next: AiModelInvokeResult) => void;
}) {
  const predictions = data.predictions ?? [];
  const first = predictions[0];
  const [showForm, setShowForm] = useState(true);
  const [product, setProduct] = useState(first?.item_name?.trim() || "LAY MASH  50");
  const [qtyOnHand, setQtyOnHand] = useState(
    first?.current_quantity != null ? String(first.current_quantity) : "178",
  );
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (first?.item_name) setProduct(first.item_name.trim());
    if (first?.current_quantity != null) setQtyOnHand(String(first.current_quantity));
  }, [first?.item_name, first?.current_quantity]);

  async function onProject(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    const qty = Number(qtyOnHand);
    if (!product.trim()) {
      setFormError("Enter a product name from your catalog.");
      return;
    }
    if (Number.isNaN(qty) || qty < 0) {
      setFormError("Quantity on hand must be a number 0 or greater.");
      return;
    }
    setBusy(true);
    try {
      const next = await invokeAiModel(resultMeta.model_id || INVENTORY_MODEL_ID, {
        item: product.trim(),
        quantity_on_hand: qty,
      });
      onResultChange?.(next);
    } catch {
      setFormError("We could not run this projection. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <ReportShell>
      {predictions.length === 0 && <p className="muted">No product results for this run.</p>}
      {predictions.map((row) => {
        const rec = recommendationLabel(row.recommendation);
        const intervals = row.prediction_intervals;
        const confidencePct = intervals?.confidence_level
          ? Math.round(intervals.confidence_level * 100)
          : 95;
        const delta =
          row.current_quantity != null && row.predicted_inventory_quantity != null
            ? row.predicted_inventory_quantity - row.current_quantity
            : null;

        return (
          <section key={row.inventory_id ?? row.item_name} className="inventory-product-card">
            <h3 className="inventory-product-name">{row.item_name ?? "Product"}</h3>
            <dl className="inventory-stats">
              <div className="inventory-stat">
                <dt>On hand now</dt>
                <dd>{fmtUnits(row.current_quantity)} units</dd>
              </div>
              <div className="inventory-stat inventory-stat--highlight">
                <dt>Suggested target level</dt>
                <dd>{fmtUnits(row.predicted_inventory_quantity)} units</dd>
              </div>
              {delta != null && (
                <div className="inventory-stat">
                  <dt>Change vs today</dt>
                  <dd className={delta >= 0 ? "delta-up" : "delta-down"}>
                    {delta >= 0 ? "+" : ""}
                    {fmtUnits(delta)} units
                  </dd>
                </div>
              )}
            </dl>
            <div className={`inventory-recommendation ${rec.tone}`}>
              <span className="inventory-recommendation-badge">{row.recommendation ?? "—"}</span>
              <p>{rec.label}</p>
            </div>
            {intervals && (
              <div className="inventory-range">
                <h4>Likely range ({confidencePct}% confidence)</h4>
                <p>
                  Between <strong>{fmtUnits(intervals.lower)}</strong> and{" "}
                  <strong>{fmtUnits(intervals.upper)}</strong> units (best guess{" "}
                  <strong>{fmtUnits(intervals.prediction ?? row.predicted_inventory_quantity)}</strong>
                  ).
                </p>
              </div>
            )}
            <ul className="inventory-risks">
              {row.stockout_risk != null && (
                <li>
                  <strong>Run-out risk:</strong> {fmtPct01(row.stockout_risk)}
                </li>
              )}
              {row.overstock_risk != null && (
                <li>
                  <strong>Overstock risk:</strong> {fmtPct01(row.overstock_risk)}
                </li>
              )}
              {row.confidence_score != null && (
                <li>
                  <strong>Model confidence:</strong> {fmtPct01(row.confidence_score)}
                </li>
              )}
            </ul>
          </section>
        );
      })}

      <section className="model-try-panel">
        <h3 className="model-try-title">Use this model</h3>
        <p className="buy-server-product-note">
          <button
            type="button"
            className="linkish"
            onClick={() => setShowForm((v) => !v)}
          >
            {showForm ? "Hide model form" : "Click here for a model form"}
          </button>{" "}
          — enter a product and quantity on hand to project how much you need.
        </p>

        {showForm && (
          <form className="deposit-form buy-server-form model-try-form" onSubmit={onProject}>
            <div className="buy-server-row">
              <label htmlFor="inv-try-product">Product</label>
              <input
                id="inv-try-product"
                type="text"
                required
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                placeholder="LAY MASH  50"
                disabled={busy}
              />
            </div>
            <div className="buy-server-row">
              <label htmlFor="inv-try-qty">Qty on hand</label>
              <input
                id="inv-try-qty"
                type="number"
                min={0}
                step={1}
                required
                value={qtyOnHand}
                onChange={(e) => setQtyOnHand(e.target.value)}
                placeholder="178"
                disabled={busy}
              />
            </div>
            {formError && (
              <p className="model-run-error" role="alert">
                {formError}
              </p>
            )}
            <div className="buy-server-actions">
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? "Projecting…" : "Project quantity needed"}
              </button>
            </div>
          </form>
        )}
      </section>
    </ReportShell>
  );
}

function VendorMinPoReport({
  data,
  resultMeta,
  onResultChange,
}: {
  data: VendorMinPoPayload;
  resultMeta: AiModelInvokeResult;
  onResultChange?: (next: AiModelInvokeResult) => void;
}) {
  const lines = data.order_items ?? [];
  const orderTotal = lines.reduce((sum, line) => sum + (line.line_total ?? 0), 0);
  const min = data.minimum_order_value ?? 2500;
  const gap = Math.max(0, min - orderTotal);

  const [showForm, setShowForm] = useState(true);
  const [productId, setProductId] = useState(String(VENDOR_PO_PRODUCTS[0]?.inventory_id ?? 1));
  const selectedProduct =
    VENDOR_PO_PRODUCTS.find((p) => String(p.inventory_id) === productId) || VENDOR_PO_PRODUCTS[0];
  const [qtyOnHand, setQtyOnHand] = useState(String(selectedProduct?.quantity_on_hand ?? 0));
  const [vendorMin, setVendorMin] = useState(String(min || 2500));
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    const p = VENDOR_PO_PRODUCTS.find((x) => String(x.inventory_id) === productId);
    if (p) setQtyOnHand(String(p.quantity_on_hand));
  }, [productId]);

  async function onProject(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    const qty = Number(qtyOnHand);
    const minimum = Number(vendorMin);
    const id = Number(productId);
    if (!selectedProduct) {
      setFormError("Select a product from the vendor list.");
      return;
    }
    if (Number.isNaN(qty) || qty < 0) {
      setFormError("Quantity on hand must be a number 0 or greater.");
      return;
    }
    if (Number.isNaN(minimum) || minimum <= 0) {
      setFormError("Vendor PO minimum must be a positive dollar amount.");
      return;
    }
    setBusy(true);
    try {
      const next = await invokeAiModel(resultMeta.model_id || "allpet-vendor-minpovalue", {
        vendor_minimum: minimum,
        client_name: "allpet",
        location_id: 9999,
        items: vendorPoItemsPayload(id, qty),
      });
      onResultChange?.(next);
    } catch {
      setFormError("We could not run this vendor PO projection. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <ReportShell
      kicker="Vendor PO"
      title={SAMPLE_VENDOR_NAME}
      subtitle={SAMPLE_STORE_LABEL}
    >
      <p className="vendor-po-minimum-line">
        Requesting a PO minimum of <strong>{fmtMoney(min)}</strong>
      </p>

      <div
        className={`inventory-recommendation ${data.meets_minimum ? "rec-hold" : "rec-increase"}`}
      >
        <span className="inventory-recommendation-badge">
          {data.meets_minimum ? "Ready to submit" : "Almost there"}
        </span>
        <p>
          {data.meets_minimum
            ? `Your suggested order totals ${fmtMoney(orderTotal)} and meets the vendor minimum.`
            : `Suggested order: ${fmtMoney(orderTotal)}. You are ${fmtMoney(gap)} short of the ${fmtMoney(min)} minimum — add a small quantity to one line item.`}
        </p>
      </div>

      <h3 className="vendor-po-lines-heading">Suggested order lines</h3>
      <div className="vendor-po-lines">
        {lines.map((line, i) => (
          <section key={line.item_name ?? i} className="inventory-product-card vendor-po-line-card">
            <h4 className="inventory-product-name">{line.item_name?.trim() ?? "Product"}</h4>
            <dl className="inventory-stats">
              <div className="inventory-stat inventory-stat--highlight">
                <dt>Quantity to order</dt>
                <dd>{fmtUnits(line.quantity_to_order)} units</dd>
              </div>
              <div className="inventory-stat">
                <dt>Your cost</dt>
                <dd>{fmtMoney(line.unit_cost)}</dd>
              </div>
              <div className="inventory-stat">
                <dt>Retail price</dt>
                <dd>{fmtMoney(line.unit_price)}</dd>
              </div>
              <div className="inventory-stat">
                <dt>Line total</dt>
                <dd>{fmtMoney(line.line_total)}</dd>
              </div>
            </dl>
          </section>
        ))}
      </div>

      <div className="vendor-po-total">
        <span>Order subtotal</span>
        <strong>{fmtMoney(orderTotal)}</strong>
      </div>

      <section className="model-try-panel">
        <h3 className="model-try-title">Use this model</h3>
        <p className="buy-server-product-note">
          <button type="button" className="linkish" onClick={() => setShowForm((v) => !v)}>
            {showForm ? "Hide model form" : "Click here for a model form"}
          </button>{" "}
          — pick a product from the 16-item vendor list, set quantity on hand, and see how many to
          order to meet the PO minimum.
        </p>

        {showForm && (
          <form className="deposit-form buy-server-form model-try-form" onSubmit={onProject}>
            <div className="buy-server-row">
              <label htmlFor="vendor-try-product">Product</label>
              <select
                id="vendor-try-product"
                required
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                disabled={busy}
              >
                {VENDOR_PO_PRODUCTS.map((p) => (
                  <option key={p.inventory_id} value={p.inventory_id}>
                    {p.item_name.trim()}
                  </option>
                ))}
              </select>
            </div>
            <div className="buy-server-row">
              <label htmlFor="vendor-try-qty">Qty on hand</label>
              <input
                id="vendor-try-qty"
                type="number"
                min={0}
                step={1}
                required
                value={qtyOnHand}
                onChange={(e) => setQtyOnHand(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="buy-server-row">
              <label htmlFor="vendor-try-min">PO minimum ($)</label>
              <input
                id="vendor-try-min"
                type="number"
                min={1}
                step={0.01}
                required
                value={vendorMin}
                onChange={(e) => setVendorMin(e.target.value)}
                disabled={busy}
              />
            </div>
            {selectedProduct && (
              <p className="buy-server-product-note">
                Cost {fmtMoney(selectedProduct.cost)} · retail {fmtMoney(selectedProduct.price)} ·
                the model returns how many units to order toward the vendor minimum.
              </p>
            )}
            {formError && (
              <p className="model-run-error" role="alert">
                {formError}
              </p>
            )}
            <div className="buy-server-actions">
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? "Projecting…" : "Project order to meet minimum"}
              </button>
            </div>
          </form>
        )}
      </section>
    </ReportShell>
  );
}

function OnOffBadge({ on }: { on: boolean }) {
  return (
    <span className={`sltp-onoff ${on ? "sltp-onoff--on" : "sltp-onoff--off"}`}>
      {on ? "On" : "Off"}
    </span>
  );
}

function sltpActionSummary(action: string | undefined): string {
  const key = (action ?? "").toLowerCase();
  if (key === "exit_trail_take_profit") {
    return "Exit on trail take profit — the model suggests closing using your trailing profit rules.";
  }
  if (key.startsWith("exit")) {
    return "Consider exiting — risk or profit rules triggered an exit signal.";
  }
  if (key.includes("hold")) {
    return "Hold the position — no exit signal right now.";
  }
  return humanizeAction(action);
}

function StockSltpReport({ data }: { data: StockSltpPayload }) {
  const stock = str(data, "stock") ?? str(data, "symbol") ?? "—";
  const timeframe = str(data, "timeframe");
  const subtitle = timeframe ? `${stock} · ${timeframe} chart` : stock;

  const stopLoss = num(data, "stop_loss_percent");
  const takeProfit = num(data, "take_profit_percent");
  const trailTp = num(data, "trail_take_profit_percent");

  const maxInvest = num(data, "max_investment_usd");
  const recommended = num(data, "recommended_investment_usd") ?? num(data, "recommended_investment");
  const investPct =
    maxInvest != null && maxInvest > 0 && recommended != null
      ? Math.round((recommended / maxInvest) * 100)
      : num(data, "recommended_position_pct");

  const profitFollow = bool(data, "profit_follow_mode");
  const trailEnabled = bool(data, "trail_take_profit_enabled");
  const allowNew = bool(data, "allow_new_position");

  const probUp = num(data, "prob_up_percent");
  const probDown = num(data, "prob_down_percent");
  const direction = str(data, "predicted_direction");
  const action = str(data, "position_action");

  return (
    <ReportShell kicker="Stock volatility & exits" title={stock} subtitle={subtitle}>
      <div className="sltp-levels">
        <div className="sltp-level">
          <span className="sltp-level-label">
            Stop loss <strong className="sltp-level-value">{fmtPctWhole(stopLoss)}</strong>
          </span>
        </div>
        <div className="sltp-level">
          <span className="sltp-level-label">
            Take profit <strong className="sltp-level-value">{fmtPctWhole(takeProfit)}</strong>
          </span>
        </div>
        <div className="sltp-level">
          <span className="sltp-level-label">
            Trail take profit{" "}
            <strong className="sltp-level-value">{fmtPctWhole(trailTp)}</strong>
          </span>
        </div>
      </div>

      <div className="sltp-max-invest">
        <span className="sltp-max-invest-label">Max investment ($)</span>
        <span className="sltp-max-invest-value">
          {investPct != null ? `${investPct}%` : "—"}
          {recommended != null && (
            <>
              {" "}
              · <strong>{fmtMoney(recommended)}</strong> suggested
            </>
          )}
          {maxInvest != null && (
            <span className="sltp-max-invest-cap"> (cap {fmtMoney(maxInvest)})</span>
          )}
        </span>
      </div>

      <div className="sltp-switches">
        {profitFollow != null && (
          <div className="sltp-switch">
            <span>Profit follow mode</span>
            <OnOffBadge on={profitFollow} />
          </div>
        )}
        {trailEnabled != null && (
          <div className="sltp-switch">
            <span>Trail take profit</span>
            <OnOffBadge on={trailEnabled} />
          </div>
        )}
        {allowNew != null && (
          <div className="sltp-switch">
            <span>New positions</span>
            <OnOffBadge on={allowNew} />
          </div>
        )}
      </div>

      <div className="inventory-recommendation rec-neutral">
        <span className="inventory-recommendation-badge">{humanizeAction(action)}</span>
        <p>{sltpActionSummary(action)}</p>
      </div>

      <dl className="inventory-stats sltp-secondary-stats">
        <div className="inventory-stat">
          <dt>Outlook</dt>
          <dd>
            {direction ? direction.charAt(0).toUpperCase() + direction.slice(1) : "—"}
            {probUp != null && probDown != null && (
              <span className="sltp-prob-hint">
                {" "}
                ({fmtPctWhole(probUp)} up · {fmtPctWhole(probDown)} down)
              </span>
            )}
          </dd>
        </div>
        <div className="inventory-stat">
          <dt>Open profit</dt>
          <dd>{fmtPctNumber(num(data, "current_profit_percent"))}</dd>
        </div>
        <div className="inventory-stat">
          <dt>Model confidence</dt>
          <dd>{fmtPct01(num(data, "confidence"))}</dd>
        </div>
        <div className="inventory-stat">
          <dt>Take profit now</dt>
          <dd>{bool(data, "execute_take_profit") ? "Yes" : "No"}</dd>
        </div>
      </dl>
    </ReportShell>
  );
}

function humanizeThreatClass(label: string | undefined): string {
  if (!label) return "Unknown";
  return label.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function majorityClassFromByIp(rows: WebTrafficIpRow[]): {
  predicted_class?: string;
  confidence?: number;
  benign?: number;
  scan?: number;
} {
  let scanCount = 0;
  let benignCount = 0;
  let scanConfSum = 0;
  let benignConfSum = 0;
  for (const row of rows) {
    const n = row.count ?? 0;
    const cls = (row.predicted_class || "").toLowerCase();
    const conf = row.confidence;
    if (cls === "scan") {
      scanCount += n;
      if (conf != null) scanConfSum += conf * n;
    } else if (cls === "benign") {
      benignCount += n;
      if (conf != null) benignConfSum += conf * n;
    }
  }
  const total = scanCount + benignCount;
  if (!total) return {};
  if (scanCount >= benignCount) {
    return {
      predicted_class: "scan",
      confidence: scanCount ? scanConfSum / scanCount : undefined,
      scan: scanCount / total,
      benign: benignCount / total,
    };
  }
  return {
    predicted_class: "benign",
    confidence: benignCount ? benignConfSum / benignCount : undefined,
    scan: scanCount / total,
    benign: benignCount / total,
  };
}

function utcDateKey(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso.slice(0, 10);
  }
  return d.toISOString().slice(0, 10);
}

function todayUtcDateKey(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Prefer calendar today (UTC); if empty, fall back to log_range.end / latest window day. */
function resolveTodayKey(rows: WebTrafficIpRow[], logEnd?: string): string {
  const today = todayUtcDateKey();
  const hasToday = rows.some((r) => utcDateKey(r.window?.window_start) === today);
  if (hasToday) return today;
  if (logEnd && rows.some((r) => utcDateKey(r.window?.window_start) === logEnd)) {
    return logEnd;
  }
  let latest = "";
  for (const r of rows) {
    const k = utcDateKey(r.window?.window_start);
    if (k && k > latest) latest = k;
  }
  return latest || today;
}

function formatServerResponse(row: WebTrafficIpRow): string {
  const w = row.window;
  if (!w) return "—";
  const parts: string[] = [];
  if ((w.status_2xx ?? 0) > 0) parts.push(`${w.status_2xx}×2xx`);
  if ((w.status_4xx ?? 0) > 0) parts.push(`${w.status_4xx}×4xx`);
  if ((w.status_5xx ?? 0) > 0) parts.push(`${w.status_5xx}×5xx`);
  if (!parts.length) {
    const z2 = w.status_2xx ?? 0;
    const z4 = w.status_4xx ?? 0;
    const z5 = w.status_5xx ?? 0;
    if (z2 === 0 && z4 === 0 && z5 === 0) return "—";
    return `${z2}×2xx · ${z4}×4xx · ${z5}×5xx`;
  }
  return parts.join(" · ");
}

function WebTrafficIpTable({
  rows,
  caption,
}: {
  rows: WebTrafficIpRow[];
  caption?: string;
}) {
  if (!rows.length) {
    return <p className="muted webtraffic-meta">{caption || "No rows."}</p>;
  }
  return (
    <div className="lease-results-table-wrap webtraffic-ip-table-wrap">
      <table className="lease-results-table webtraffic-ip-table">
        <thead>
          <tr>
            <th scope="col">IP</th>
            <th scope="col">Count</th>
            <th scope="col">Country</th>
            <th scope="col">State / region</th>
            <th scope="col">City</th>
            <th scope="col">Class</th>
            <th scope="col">Confidence</th>
            <th scope="col">Path</th>
            <th scope="col">Server response</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const cls = (row.predicted_class || "").toLowerCase();
            const privateGeo =
              row.geo_status === "private" || row.geolocation === "private";
            return (
              <tr
                key={`${row.ip}-${row.window?.path_prefix || ""}-${row.window?.window_start || ""}-${row.count}`}
              >
                <td>
                  <code className="webtraffic-ip">{row.ip || "—"}</code>
                </td>
                <td>{(row.count ?? 0).toLocaleString()}</td>
                <td>{privateGeo ? "Private" : row.country?.trim() || "—"}</td>
                <td>{privateGeo ? "—" : row.region?.trim() || "—"}</td>
                <td>{privateGeo ? "—" : row.city?.trim() || "—"}</td>
                <td>
                  <span
                    className={`webtraffic-class-badge webtraffic-class-badge--${
                      cls === "scan" ? "scan" : "benign"
                    }`}
                  >
                    {humanizeThreatClass(row.predicted_class)}
                  </span>
                </td>
                <td>{fmtPct01(row.confidence)}</td>
                <td className="webtraffic-path">
                  {row.window?.method ? `${row.window.method} ` : ""}
                  {row.window?.path_prefix || "—"}
                </td>
                <td className="webtraffic-server-response">
                  {formatServerResponse(row)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function WebTrafficThreatReport({ data }: { data: WebTrafficThreatPayload }) {
  const byIp = [...(data.by_ip || [])].sort(
    (a, b) => (b.count ?? 0) - (a.count ?? 0),
  );
  const todayKey = resolveTodayKey(byIp, data.log_range?.end);
  const todayRows = byIp.filter(
    (r) => utcDateKey(r.window?.window_start) === todayKey,
  );
  const fromRows = majorityClassFromByIp(byIp);
  const predicted = (
    data.predicted_class ||
    fromRows.predicted_class ||
    ""
  ).toLowerCase();
  const title = humanizeThreatClass(predicted || data.predicted_class);
  const confidence = data.confidence ?? fromRows.confidence;
  const pBenign = data.probabilities?.benign ?? fromRows.benign;
  const pScan = data.probabilities?.scan ?? fromRows.scan;
  const client = (data.client_name || "chepito").trim();
  const isScan = predicted === "scan";
  const tone = isScan ? "rec-increase" : "rec-hold";
  const rangeStart = data.log_range?.start;
  const rangeEnd = data.log_range?.end;
  const summary =
    confidence != null
      ? `Overall traffic classified as ${predicted || "unknown"} (${fmtPct01(confidence)} confidence)` +
        (data.request_count != null
          ? ` across ${data.request_count.toLocaleString()} requests.`
          : ".")
      : `Overall traffic classified as ${predicted || "unknown"}.`;

  const metrics = data.metrics_test;
  const source = data.data_source;
  const metaBits: string[] = [];
  if (metrics?.n_train != null && metrics?.n_test != null) {
    metaBits.push(`Train ${metrics.n_train} · test ${metrics.n_test}`);
  }
  if (metrics?.macro_f1 != null) {
    metaBits.push(`Macro F1 ${(metrics.macro_f1 * 100).toFixed(1)}%`);
  }
  if (source?.n_raw_events != null) {
    metaBits.push(`${source.n_raw_events.toLocaleString()} raw events`);
  }
  if (source?.n_windows != null) {
    metaBits.push(`${source.n_windows.toLocaleString()} windows`);
  }

  return (
    <ReportShell kicker="Web Security" title={title} subtitle={client}>
      <div className={`inventory-recommendation ${tone}`}>
        <span className="inventory-recommendation-badge">{title}</span>
        <p>{summary}</p>
      </div>
      <dl className="inventory-stats webtraffic-stats">
        <div className="inventory-stat inventory-stat--highlight">
          <dt>Confidence</dt>
          <dd>{fmtPct01(confidence)}</dd>
        </div>
        <div className="inventory-stat">
          <dt>Benign</dt>
          <dd>{fmtPct01(pBenign)}</dd>
        </div>
        <div className="inventory-stat">
          <dt>Scan</dt>
          <dd>{fmtPct01(pScan)}</dd>
        </div>
        <div className="inventory-stat">
          <dt>Provenance</dt>
          <dd className="webtraffic-stat-text">
            {(data.label_provenance || "—").replace(/_/g, " ")}
          </dd>
        </div>
        <div className="inventory-stat">
          <dt>ML</dt>
          <dd>{data.ml_enabled ? "On" : "Off"}</dd>
        </div>
      </dl>
      {metaBits.length > 0 ? (
        <p className="muted webtraffic-meta">{metaBits.join(" · ")}</p>
      ) : null}

      {byIp.length > 0 ? (
        <>
          <section className="webtraffic-ip-section">
            <h3 className="webtraffic-ip-heading">Today&apos;s requests by IP</h3>
            <p className="webtraffic-log-range" role="status">
              Day: <strong>{todayKey || "—"}</strong>
              {todayRows.length
                ? ` · ${todayRows.length.toLocaleString()} IP${todayRows.length === 1 ? "" : "s"}`
                : " · no windows for this day"}
            </p>
            <WebTrafficIpTable
              rows={todayRows}
              caption="No requests for today in this response."
            />
          </section>

          <section className="webtraffic-ip-section">
            <h3 className="webtraffic-ip-heading">All requests by IP</h3>
            <p className="webtraffic-log-range" role="status">
              Log date range:{" "}
              <strong>{rangeStart || "—"}</strong>
              {" → "}
              <strong>{rangeEnd || "—"}</strong>
            </p>
            <WebTrafficIpTable rows={byIp} />
            <p className="muted webtraffic-meta">
              {byIp.length.toLocaleString()} IP
              {byIp.length === 1 ? "" : "s"} classified
            </p>
          </section>
        </>
      ) : (
        <p className="muted webtraffic-meta">
          No per-IP breakdown in this response. Re-run with a log date range
          (start / end) to load IP counts and geolocation.
        </p>
      )}
    </ReportShell>
  );
}

function GenericSummaryReport({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data).filter(
    ([key]) =>
      !key.includes("client") &&
      !key.includes("path") &&
      !key.includes("url") &&
      !key.includes("ip") &&
      !key.includes("host") &&
      typeof data[key] !== "object",
  );

  return (
    <ReportShell kicker="Model result" title="Summary" subtitle={`Sample customer · ${SAMPLE_STORE_LABEL}`}>
      {entries.length === 0 ? (
        <p className="muted">No simple summary available. Try another model.</p>
      ) : (
        <dl className="inventory-stats">
          {entries.slice(0, 12).map(([key, value]) => (
            <div className="inventory-stat" key={key}>
              <dt>{key.replace(/_/g, " ")}</dt>
              <dd>{String(value)}</dd>
            </div>
          ))}
        </dl>
      )}
    </ReportShell>
  );
}

type Props = {
  result: AiModelInvokeResult;
  onResultChange?: (next: AiModelInvokeResult) => void;
};

export function ModelResultView({ result, onResultChange }: Props) {
  const json = result.upstream_json;

  if (json && typeof json === "object" && !Array.isArray(json)) {
    const record = json as Record<string, unknown>;
    if (record.status === "error" && record.error) {
      return (
        <div className="model-run-error" role="alert">
          <p>{String(record.error)}</p>
          <p className="muted">Try another symbol or contact us for a custom model.</p>
        </div>
      );
    }
  }

  if (isInventoryPredict(json)) {
    return (
      <InventoryPredictReport
        data={json}
        resultMeta={result}
        onResultChange={onResultChange}
      />
    );
  }
  if (isVendorMinPo(json)) {
    return (
      <VendorMinPoReport
        data={json}
        resultMeta={result}
        onResultChange={onResultChange}
      />
    );
  }
  if (isStockSltp(json)) {
    return <StockSltpReport data={json} />;
  }
  if (isWebTrafficThreat(json)) {
    return <WebTrafficThreatReport data={json} />;
  }
  if (json && typeof json === "object" && !Array.isArray(json)) {
    return <GenericSummaryReport data={json as Record<string, unknown>} />;
  }

  return <p className="muted">No readable result. Please try again later.</p>;
}
